<template>
  <div class="mt-1">
    <b-card>
      <b-overlay :show="loading" rounded="sm" no-fade>
        <b-row class="mb-1">
          <b-col
            cols="12"
            md="7"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <b-button
              variant="outline-primary"
              class="btn-icon"
              :to="{ name: 'events' }"
              ><i class="fa-solid fa-arrow-left"></i
              ><span class="ml-25">Back</span></b-button
            >
            <h3 class="ml-1">Edit Event</h3>
          </b-col>
          <b-col
            cols="12"
            md="5"
            class="d-flex align-items-center justify-content-end"
          >
            <b-button
              v-if="loaded && ['open','closed'].includes((form.status || '').toLowerCase())"
              variant="outline-success"
              size="sm"
              :disabled="sendingInvitations"
              @click="sendInvitations"
            >
              <i class="fa-solid fa-envelope mr-25"></i>
              {{ sendingInvitations ? 'Sending...' : 'Send Invitations' }}
            </b-button>
          </b-col>
        </b-row>

        <b-form v-if="loaded" @submit.prevent="onSubmit">
          <b-alert
            v-if="(form.status || '').toLowerCase() === 'draft'"
            variant="warning"
            show
            class="mb-1"
          >
            This event is in draft. Users won't be able to view it until the status is set to "open".
          </b-alert>

          <app-collapse accordion :type="'border'" class="mb-1">
            <!-- Event Details -->
            <app-collapse-item title="Event Details" :isVisible="true">
              <label>Course *</label>
              <v-select
                class="flex-grow-1 mb-1"
                :options="options.courses"
                label="title"
                :reduce="(c) => c.id"
                v-model="form.course_id"
                required
              />
              <b-row>
                <b-col md="4"
                  ><b-form-group label="Year *"
                    ><b-form-input
                      type="number"
                      v-model.number="form.year"
                      min="2000"
                      required/></b-form-group
                ></b-col>
                <b-col md="4"
                  ><b-form-group label="Time zone *"
                    ><b-form-input
                      v-model="form.time_zone"
                      required/></b-form-group
                ></b-col>
                <b-col md="4"
                  ><b-form-group label="Status"
                    ><v-select
                      :options="options.enums.event_statuses"
                      v-model="form.status"
                      :reduce="(v) => v"/></b-form-group
                ></b-col>
              </b-row>

              <div class="d-flex justify-content-between align-items-center parts-header">
                <h5 class="mb-0">Parts</h5>
                <b-button size="sm" variant="primary" @click="addSession">+ Add part</b-button>
              </div>
              <div
                v-for="(s, idx) in sessions"
                :key="s.id ? s.id : idx"
                class="session-card-edit mb-1"
              >
                <div class="d-flex justify-content-between align-items-center session-edit-header">
                  <h6 class="mb-0">{{ getPartTitle(idx) }}</h6>
                  <div>
                    <b-button
                      size="sm"
                      class="mr-50"
                      variant="outline-primary"
                      @click="saveSession(s)"
                      >Save</b-button
                    >
                    <b-button
                      size="sm"
                      class="mr-50"
                      variant="outline-secondary"
                      v-if="canManageAttendance() && s.id"
                      :to="{
                        name: 'event-attendance',
                        params: { sessionId: s.id },
                      }"
                      :disabled="!s.id"
                      >Attendance</b-button
                    >
                    <b-button
                      size="sm"
                      variant="outline-danger"
                      v-if="sessions.length > 1"
                      @click="deleteSession(s)"
                      >Remove</b-button
                    >
                  </div>
                </div>
                <b-row class="mt-50">
                  <b-col md="4"
                    ><label>Start</label>
                    <b-form-input type="date" v-model="s.start_date" />
                    <b-form-input
                      class="mt-25"
                      type="text"
                      v-model="s.start_time"
                      placeholder="HH:mm"
                      @blur="onTimeBlur(s, 'start_time')"
                    />
                  </b-col>
                  <b-col md="4"
                    ><label>End</label>
                    <b-form-input type="date" v-model="s.end_date" />
                    <b-form-input
                      class="mt-25"
                      type="text"
                      v-model="s.end_time"
                      placeholder="HH:mm"
                      @blur="onTimeBlur(s, 'end_time')"
                    />
                  </b-col>
                  <b-col md="4">
                    <div
                      class="d-flex justify-content-between align-items-center"
                    >
                      <label>Venue</label>
                      <a
                        href="#"
                        @click.prevent="openAddVenueModal(idx)"
                        class="text-primary text-decoration-underline"
                        style="font-size: 0.875rem;"
                        >Add Venue</a
                      >
                    </div>
                    <v-select
                      :options="options.venues"
                      label="name"
                      :reduce="(v) => v.id"
                      v-model="s.venue_id"
                    />
                  </b-col>
                </b-row>
                <b-row class="mt-50">
                  <b-col md="6"
                    ><label>Meeting URL</label
                    ><b-form-input v-model="s.meeting_url"
                  /></b-col>
                  <b-col md="3"
                    ><label>Passcode</label
                    ><b-form-input v-model="s.meeting_passcode"
                  /></b-col>
                  <b-col md="3">
                    <div
                      class="d-flex justify-content-between align-items-center"
                    >
                      <label>Trainer</label>
                      <a
                        href="#"
                        @click.prevent="openAddTrainerModal(idx)"
                        class="text-primary text-decoration-underline"
                        style="font-size: 0.875rem;"
                        >Add Trainer</a
                      >
                    </div>
                    <v-select
                      :options="options.trainers"
                      label="name"
                      :reduce="(t) => t.id"
                      v-model="s.primary_trainer_id"
                    />
                  </b-col>
                </b-row>

                <div class="mt-50">
                  <div class="d-flex justify-content-between align-items-center">
                    <label class="mb-0">Add-ons</label>
                    <b-button
                      size="sm"
                      variant="outline-primary"
                      @click="addAddon(s)"
                    >+ Add add-on</b-button>
                  </div>

                  <div
                    v-if="!s.addons || s.addons.length === 0"
                    class="text-muted small mt-25"
                  >
                    No add-ons
                  </div>

                  <div
                    v-for="(a, aidx) in s.addons"
                    :key="`addon-${idx}-${aidx}`"
                    class="border rounded p-50 mt-50"
                  >
                    <div class="d-flex justify-content-between align-items-center mb-25">
                      <strong class="small">Add-on {{ aidx + 1 }}</strong>
                      <b-button
                        size="sm"
                        variant="outline-danger"
                        @click="removeAddon(s, aidx)"
                      >Remove</b-button>
                    </div>
                    <b-row>
                      <b-col md="4">
                        <label>Code *</label>
                        <b-form-input v-model="a.code" placeholder="lunch" />
                      </b-col>
                      <b-col md="8">
                        <label>Label *</label>
                        <b-form-input v-model="a.label" placeholder="Lunch" />
                      </b-col>
                    </b-row>
                    <b-row class="mt-25">
                      <b-col md="6">
                        <label>Starts time (optional)</label>
                        <b-form-input
                          type="text"
                          v-model="a.starts_time"
                          placeholder="HH:mm"
                          @blur="onAddonTimeBlur(a, 'starts_time')"
                        />
                        <div class="text-muted small mt-25">Uses session start date</div>
                      </b-col>
                      <b-col md="6">
                        <label>Ends time (optional)</label>
                        <b-form-input
                          type="text"
                          v-model="a.ends_time"
                          placeholder="HH:mm"
                          @blur="onAddonTimeBlur(a, 'ends_time')"
                        />
                        <div class="text-muted small mt-25">Uses session end date</div>
                      </b-col>
                    </b-row>
                  </div>
                </div>
              </div>

              <b-row class="mt-1">
                <b-col md="3"
                  ><b-form-group label="Max participants"
                    ><b-form-input
                      type="number"
                      min="1"
                      v-model.number="form.max_participants"/></b-form-group
                ></b-col>
          
                <b-col md="3" class="d-flex align-items-center"
                  ><b-form-checkbox
                    v-model="form.require_manager_approval"
                    :disabled="form.is_pre_approved"
                    >Require manager approval</b-form-checkbox
                  ></b-col
                >

                <b-col md="3" class="d-flex align-items-center"
                  ><b-form-checkbox
                    v-model="form.require_admin_approval"
                    :disabled="form.is_pre_approved"
                    >Require admin approval</b-form-checkbox
                  ></b-col
                >

                      <b-col md="3" class="d-flex align-items-center"
                  ><b-form-checkbox v-model="form.is_pre_approved"
                    >Mandatory Training</b-form-checkbox
                  ></b-col
                >
              </b-row>

              <b-row
                v-if="approvalWarningShown && missingApprovalSelection"
                class="mt-25"
              >
                <b-col md="12">
                  <small class="text-danger">
                    Select at least one of: Require manager approval, Require
                    admin approval, or Mandatory Training.
                  </small>
                </b-col>
              </b-row>

              <b-row class="mt-50">
                <b-col md="12" class="d-flex align-items-center">
                  <b-form-checkbox v-model="form.is_eft">
                    Educational Fee Training (EFT)
                  </b-form-checkbox>
                </b-col>
              </b-row>

              <b-form-group label="Title *"
                ><b-form-input v-model="form.title_override" required
              /></b-form-group>
              <b-form-group label="Description">
                <quill-editor
                  v-model="form.description_override"
                  :options="descriptionEditorOption"
                  class="event-description-editor"
                />
              </b-form-group>
              <b-form-group label="Survey URL" description="Optional. Participants who confirmed attendance will receive this survey link after the last session ends.">
                <b-form-input v-model="form.survey_url" placeholder="https://..." type="text" />
              </b-form-group>

              <b-card class="mt-1" title="Preview">
                <div>
                  <div class="mb-25">
                    <strong>Course:</strong>
                    {{ (selectedCourse && selectedCourse.title) || '-' }}
                  </div>
                  <div class="mb-25">
                    <strong>Aim:</strong>
                    {{ (selectedCourse && selectedCourse.aim) || '-' }}
                  </div>
                  <div class="mb-25">
                    <strong>Learning outcomes:</strong>
                    <ul class="mb-0">
                      <li
                        v-for="(lo, i) in (selectedCourse &&
                          selectedCourse.learning_outcomes) ||
                          []"
                        :key="i"
                      >
                        {{ lo }}
                      </li>
                    </ul>
                  </div>
                </div>
              </b-card>
            </app-collapse-item>

            <!-- Application Methods -->
            <app-collapse-item title="Application Methods">
              <b-row>
                <b-col md="6"
                  ><b-form-group label="Application method"
                    ><v-select
                      :options="options.enums.application_methods"
                      v-model="form.application_method"
                      :reduce="(v) => v"
                      /></b-form-group
                ></b-col>
                <b-col md="6"
                  ><b-form-group label="Application deadline"
                    ><b-form-input
                      type="date"
                      v-model="form.application_deadline"/></b-form-group
                ></b-col>
              </b-row>
            </app-collapse-item>

            <!-- Materials -->
            <app-collapse-item title="Materials">
              <b-table
                small
                responsive
                bordered
                :items="materials"
                :fields="materialsFields"
                show-empty
                empty-text="No materials"
              >
                <template #cell(title)="{ item }">
                  <div v-if="editingId !== item.id">{{ item.title }}</div>
                  <b-form-input v-else v-model="editRow.title" />
                </template>
                <template #cell(link)="{ item }">
                  <div v-if="item.external_url">
                    <a :href="item.external_url" target="_blank">External</a>
                  </div>
                  <div v-else-if="item.file_path">
                    <a :href="materialFileHref(item.file_path)" target="_blank">Download</a>
                  </div>
                  <div v-else>-</div>
                  <div v-if="editingId === item.id" class="mt-50">
                    <b-form-input
                      v-model="editRow.external_url"
                      placeholder="External URL"
                      class="mb-50"
                    />
                    <b-form-file
                      v-model="editRow.file"
                      browse-text="Choose file"
                      placeholder="No file chosen"
                    />
                  </div>
                </template>
                <template #cell(visible)="{ item }">
                  <div v-if="editingId !== item.id">
                    {{ item.visible_from ? dateFormat(item.visible_from) : '-' }}
                    to
                    {{ item.visible_to ? dateFormat(item.visible_to) : '-' }}
                  </div>
                  <div v-else>
                    <b-form-input type="date" v-model="editRow.visible_from" class="mb-25" />
                    <b-form-input type="date" v-model="editRow.visible_to" :min="editRow.visible_from" />
                  </div>
                </template>
                <template #cell(accepted)="{ item }">
                  <div v-if="editingId !== item.id">
                    <b-badge
                      :variant="item.is_visible_to_accepted_only ? 'light-info' : 'light-secondary'"
                      >{{ item.is_visible_to_accepted_only ? 'Yes' : 'No' }}</b-badge>
                  </div>
                  <div v-else>
                    <b-form-checkbox v-model="editRow.is_visible_to_accepted_only">Accepted only</b-form-checkbox>
                  </div>
                </template>
                <template #cell(course)="{ item }">{{ (item.course && item.course.title) || '-' }}</template>
                <template #cell(actions)="{ item }">
                  <div v-if="canEditEvent()">
                    <div v-if="editingId !== item.id">
                      <b-button size="sm" variant="outline-primary" class="mr-25" @click="editMaterialStart(item)">Edit</b-button>
                      <b-button size="sm" variant="outline-danger" @click="deleteMaterial(item)">Delete</b-button>
                    </div>
                    <div v-else>
                      <b-button size="sm" variant="primary" class="mr-25" @click="editMaterialSave(item)">Save</b-button>
                      <b-button size="sm" variant="outline-secondary" @click="editMaterialCancel">Cancel</b-button>
                    </div>
                  </div>
                  <div v-else>-</div>
                </template>
              </b-table>

              <hr />
              <h6 v-if="canEditEvent()">Add new material</h6>
              <b-form v-if="canEditEvent()" @submit.prevent="uploadMaterial">
                <b-form-group label="Title"
                  ><b-form-input v-model="materialForm.title" required
                /></b-form-group>
                <b-form-group label="External URL (optional)"
                  ><b-form-input
                    v-model="materialForm.external_url"
                    placeholder="https://..."
                /></b-form-group>
                <b-form-group label="File (optional)"
                  ><b-form-file
                    v-model="materialForm.file"
                    browse-text="Choose file"
                    placeholder="No file chosen"
                /></b-form-group>
                <b-row>
                  <b-col md="6"
                    ><b-form-group label="Visible from"
                      ><b-form-input
                        type="date"
                        v-model="materialForm.visible_from"/></b-form-group
                  ></b-col>
                  <b-col md="6"
                    ><b-form-group label="Visible to"
                      ><b-form-input
                        type="date"
                        v-model="materialForm.visible_to"/></b-form-group
                  ></b-col>
                </b-row>
                <b-form-checkbox
                  v-model="materialForm.is_visible_to_accepted_only"
                  >Visible to accepted applicants only</b-form-checkbox
                >
                <div class="mt-1">
                  <b-button type="submit" variant="primary">Upload</b-button>
                </div>
              </b-form>
            </app-collapse-item>

            <!-- Target Audience -->
            <app-collapse-item title="Target Audience">
              <b-row>
                <b-col md="4">
                  <b-form-group>
                    <div
                      class="d-flex justify-content-between align-items-center"
                    >
                      <label>Companies</label>
                      <div class="text-right">
                        <a
                          v-if="form.company_ids.length === 0"
                          href="#"
                          @click.prevent="selectAllCompanies"
                          class="text-primary text-decoration-underline"
                          style="font-size: 0.875rem;"
                        >
                          Select All
                        </a>
                        <a
                          v-else
                          href="#"
                          @click.prevent="clearAllCompanies"
                          class="text-danger text-decoration-underline"
                          style="font-size: 0.875rem;"
                        >
                          Clear All
                        </a>
                      </div>
                    </div>
                    <v-select
                      multiple
                      :options="options.companies"
                      label="title"
                      :reduce="(c) => c.id"
                      v-model="form.company_ids"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="4">
                  <b-form-group>
                    <div
                      class="d-flex justify-content-between align-items-center"
                    >
                      <label>Sites</label>
                      <div class="text-right">
                        <a
                          v-if="form.site_ids.length === 0"
                          href="#"
                          @click.prevent="selectAllSites"
                          class="text-primary text-decoration-underline"
                          style="font-size: 0.875rem;"
                        >
                          Select All
                        </a>
                        <a
                          v-else
                          href="#"
                          @click.prevent="clearAllSites"
                          class="text-danger text-decoration-underline"
                          style="font-size: 0.875rem;"
                        >
                          Clear All
                        </a>
                      </div>
                    </div>
                    <v-select
                      multiple
                      :options="options.sites || []"
                      label="title"
                      :reduce="(s) => s.id"
                      v-model="form.site_ids"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="4">
                  <b-form-group>
                    <div
                      class="d-flex justify-content-between align-items-center"
                    >
                      <label>Users</label>
                      <div class="text-right">
                        <a
                          v-if="form.user_ids.length === 0"
                          href="#"
                          @click.prevent="selectAllUsers"
                          class="text-primary text-decoration-underline"
                          style="font-size: 0.875rem;"
                        >
                          Select All
                        </a>
                        <a
                          v-else
                          href="#"
                          @click.prevent="clearAllUsers"
                          class="text-danger text-decoration-underline"
                          style="font-size: 0.875rem;"
                        >
                          Clear All
                        </a>
                      </div>
                    </div>
                    <v-select
                      multiple
                      :options="options.users"
                      label="title"
                      :reduce="(u) => u.id"
                      v-model="form.user_ids"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="4">
                  <b-form-group>
                    <div
                      class="d-flex justify-content-between align-items-center"
                    >
                      <label>Roles</label>
                      <div class="text-right">
                        <a
                          v-if="form.role_ids.length === 0"
                          href="#"
                          @click.prevent="selectAllRoles"
                          class="text-primary text-decoration-underline"
                          style="font-size: 0.875rem;"
                        >
                          Select All
                        </a>
                        <a
                          v-else
                          href="#"
                          @click.prevent="clearAllRoles"
                          class="text-danger text-decoration-underline"
                          style="font-size: 0.875rem;"
                        >
                          Clear All
                        </a>
                      </div>
                    </div>
                    <v-select
                      multiple
                      :options="options.roles"
                      label="name"
                      :reduce="(r) => r.id"
                      v-model="form.role_ids"
                    />
                  </b-form-group>
                </b-col>
              </b-row>

              <div class="mt-1">
                <div class="border rounded p-75">
                  <div class="d-flex align-items-center justify-content-between">
                    <small class="text-muted">Audience preview</small>
                    <small v-if="audiencePreview.loading" class="text-muted">Loading...</small>
                    <small v-else class="text-muted">{{ audiencePreview.count }} users</small>
                  </div>
                  <small v-if="!audiencePreviewHasFilters" class="text-danger d-block mt-25">
                    if no filters are selected, all companies are in the target audience
                  </small>
                  <div v-if="audiencePreview.error" class="text-danger small mt-25">
                    Failed to load audience preview
                  </div>
                  <ul
                    v-else
                    class="small mb-0 pl-1 mt-50"
                    :style="audiencePreviewShowAll ? '' : 'max-height: 150px; overflow: auto;'"
                  >
                    <li
                      v-for="u in audiencePreviewVisibleUsers"
                      :key="u.id"
                      :title="u.full_name || (u.name ? (u.name + ' ' + (u.surname || '')) : '')"
                    >
                      {{ u.email }}
                    </li>
                  </ul>
                  <div
                    v-if="!audiencePreview.loading && audiencePreview.count > 20"
                    class="d-flex align-items-center justify-content-between mt-25"
                  >
                    <small class="text-muted">
                      <template v-if="!audiencePreviewShowAll">Showing first 20 of {{ audiencePreview.count }}</template>
                      <template v-else>Showing all {{ audiencePreview.count }}</template>
                    </small>
                    <b-button
                      variant="link"
                      size="sm"
                      class="p-0"
                      @click="audiencePreviewShowAll = !audiencePreviewShowAll"
                    >
                      {{ audiencePreviewShowAll ? 'Show less' : 'Show all' }}
                    </b-button>
                  </div>
                </div>
              </div>

              <b-card class="mt-1" title="Event administrators">
                <v-select
                  multiple
                  :options="options.users"
                  label="title"
                  :reduce="(u) => u.id"
                  v-model="admin_user_ids"
                  @input="saveAdmins"
                />
              </b-card>
            </app-collapse-item>

            <!-- Event Reminders -->
            <app-collapse-item title="Event Reminders">
              <p class="text-muted small">
                Set up automatic reminders to be sent to participants before the
                event starts.
              </p>
              <div
                v-for="(reminder, idx) in reminders"
                :key="`reminder-${idx}`"
                class="border rounded p-1 mb-1"
              >
                <div
                  class="d-flex justify-content-between align-items-center mb-2"
                >
                  <h6 class="mb-0">Reminder {{ idx + 1 }}</h6>
                  <b-button
                    size="sm"
                    variant="outline-danger"
                    @click="removeReminder(idx)"
                    >Remove</b-button
                  >
                </div>
                <b-row>
                  <b-col md="4">
                    <label>Type *</label>
                    <v-select
                      :options="reminderTypeOptions"
                      label="label"
                      v-model="reminder.type"
                      :reduce="(opt) => opt.value"
                    />
                  </b-col>
                  <b-col md="4">
                    <label>Days Before Event *</label>
                    <b-form-input
                      type="number"
                      v-model.number="reminder.days_before"
                      min="1"
                      max="30"
                      placeholder="e.g. 3"
                      required
                    />
                  </b-col>
                  <b-col md="8">
                    <label>Custom Message (Optional)</label>
                    <b-form-input
                      v-model="reminder.message"
                      placeholder="Custom reminder message..."
                    />
                  </b-col>
                </b-row>
              </div>
              <b-button size="sm" variant="outline-primary" @click="addReminder"
                >+ Add Reminder</b-button
              >
            </app-collapse-item>
          </app-collapse>

          <div class="mt-2">
            <b-button type="submit" variant="primary">Update</b-button>
            <b-button
              variant="outline-secondary"
              class="ml-1"
              :to="{ name: 'events' }"
              >Cancel</b-button
            >
          </div>
        </b-form>
      </b-overlay>
    </b-card>

    <!-- Add Venue Modal -->
    <b-modal
      v-model="showAddVenueModal"
      title="Add New Venue"
      @ok="handleAddVenue"
      @cancel="resetVenueForm"
      ok-title="Add"
      cancel-title="Cancel"
    >
      <b-form-group label="Name *" label-for="venue-name">
        <b-form-input
          id="venue-name"
          v-model="newVenue.name"
          placeholder="Enter venue name"
          required
        />
      </b-form-group>
    </b-modal>

    <!-- Add Trainer Modal -->
    <b-modal
      v-model="showAddTrainerModal"
      title="Add New Trainer"
      @ok="handleAddTrainer"
      @cancel="resetTrainerForm"
      ok-title="Add"
      cancel-title="Cancel"
    >
      <b-form-group label="Name *" label-for="trainer-name">
        <b-form-input
          id="trainer-name"
          v-model="newTrainer.name"
          placeholder="Enter trainer name"
          required
        />
      </b-form-group>
      <b-form-group label="Email *" label-for="trainer-email">
        <b-form-input
          id="trainer-email"
          v-model="newTrainer.email"
          type="email"
          placeholder="Enter trainer email"
          pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
          required
        />
      </b-form-group>
      <b-form-group label="Phone" label-for="trainer-phone">
        <b-form-input
          id="trainer-phone"
          v-model="newTrainer.phone"
          placeholder="Enter trainer phone"
        />
      </b-form-group>
      <b-form-group label="CV (PDF)" label-for="trainer-cv">
        <b-form-file
          id="trainer-cv"
          v-model="newTrainerCV"
          accept=".pdf"
          placeholder="Choose a PDF file or drop it here..."
          drop-placeholder="Drop PDF file here..."
        />
      </b-form-group>
    </b-modal>
  </div>
</template>
<script>
import {
  BOverlay,
  BCard,
  BRow,
  BCol,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormCheckbox,
  BButton,
  BTable,
  BFormFile,
  BBadge,
  BModal,
  BAlert,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { quillEditor } from "vue-quill-editor";
import store from "@/store";
import { onUnmounted } from "@vue/composition-api";
import eventsStoreModule from "./eventsStoreModule";
import settingsStoreModule from "@/views/settings/settingsStoreModule";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import AppCollapse from "@core/components/app-collapse/AppCollapse.vue";
import AppCollapseItem from "@core/components/app-collapse/AppCollapseItem.vue";

export default {
  components: {
    BOverlay,
    BCard,
    BRow,
    BCol,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormCheckbox,
    BButton,
    BTable,
    BFormFile,
    BBadge,
    BModal,
    BAlert,
    vSelect,
    quillEditor,
  ToastificationContent,
    AppCollapse,
    AppCollapseItem,
  },
  data() {
    return {
      loading: false,
      descriptionEditorOption: {
        modules: {
          toolbar: [
            ["bold", "italic", "underline", "strike"],
            [{ list: "ordered" }, { list: "bullet" }],
            [{ header: [1, 2, 3, false] }],
            ["link"],
            ["clean"],
          ],
        },
        placeholder: "Enter event description...",
      },
      loaded: false,
      sendingInvitations: false,

      options: {
        courses: [],
        users: [],
        trainers: [],
        venues: [],
        companies: [],
        sites: [],
        
        enums: { event_statuses: [], application_methods: [] },
      },
      // Set once a save is attempted with no approval/mandatory option picked
      approvalWarningShown: false,
      form: {
        id: null,
        course_id: null,
        year: new Date().getFullYear(),
        title_override: "",
        description_override: "",
        status: "draft",
        max_participants: null,
        application_method: null,
        application_deadline: null,
        application_description: "",
        require_manager_approval: false,
        require_admin_approval: false,
        is_pre_approved: false,
        is_eft: false,
        time_zone: "Europe/Dublin",
        notes_admin: "",
        survey_url: "",
        company_ids: [],
        site_ids: [],
        user_ids: [],
        role_ids: [],
      },
      sessions: [],
      admin_user_ids: [],
      reminders: [], // Event reminders
      reminderTypeOptions: [
        { label: "Event Reminder", value: "event_reminder" },
        { label: "Application Reminder", value: "application_reminder" },
      ],
      selectedCourse: null,
      // Modal states
      showAddVenueModal: false,
      showAddTrainerModal: false,
      currentSessionIndex: null, // Track which session is being edited
      // New item forms
      newVenue: {
        name: "",
      },
      newTrainer: {
        name: "",
        email: "",
        phone: "",
      },
      newTrainerCV: null,
      materials: [],
      materialsFields: [
        { key: "title", label: "Title", thStyle: "min-width: 200px;" },
        { key: "link", label: "Link", thStyle: "min-width: 150px;" },
        { key: "visible", label: "Visible", thStyle: "min-width: 200px;" },
        {
          key: "accepted",
          label: "Accepted only",
          thStyle: "min-width: 120px;",
        },
        { key: "course", label: "Course", thStyle: "min-width: 150px;" },
        { key: "actions", label: "Actions", thStyle: "min-width: 150px;" },
      ],
      materialForm: {
        title: "",
        file: null,
        external_url: "",
        visible_from: "",
        visible_to: "",
        is_visible_to_accepted_only: true,
        course_id: null,
      },
      editingId: null,
      editRow: {},
      applicationDescriptionLabel: "",
      applicationDescriptionPlaceholder: "",
      showApplicationDescription: false,

      audiencePreview: { loading: false, count: 0, users: [], error: null },
      audiencePreviewRequestId: 0,
      audiencePreviewTimer: null,
      audiencePreviewShowAll: false,
    };
  },
  setup() {
    const MODULE = "events";
    if (!store.hasModule(MODULE))
      store.registerModule(MODULE, eventsStoreModule);

    const SETTINGS_MODULE = "settings";
    if (!store.hasModule(SETTINGS_MODULE))
      store.registerModule(SETTINGS_MODULE, settingsStoreModule);

    onUnmounted(() => {
      if (store.hasModule(MODULE)) store.unregisterModule(MODULE);
      if (store.hasModule(SETTINGS_MODULE))
        store.unregisterModule(SETTINGS_MODULE);
    });
  },
  computed: {
    materialsURL() {
      return this.$store.state.app.materialsURL;
    },

    // At least one enrolment route must be chosen before the event can be saved
    missingApprovalSelection() {
      return (
        !this.form.require_manager_approval &&
        !this.form.require_admin_approval &&
        !this.form.is_pre_approved
      );
    },

    audiencePreviewHasFilters() {
      const c = Array.isArray(this.form.company_ids) ? this.form.company_ids.length : 0;
      const s = Array.isArray(this.form.site_ids) ? this.form.site_ids.length : 0;
      const r = Array.isArray(this.form.role_ids) ? this.form.role_ids.length : 0;
      const u = Array.isArray(this.form.user_ids) ? this.form.user_ids.length : 0;
      return c + s + r + u > 0;
    },
    audiencePreviewVisibleUsers() {
      const users = (this.audiencePreview && Array.isArray(this.audiencePreview.users))
        ? this.audiencePreview.users
        : [];
      return this.audiencePreviewShowAll ? users : users.slice(0, 20);
    },
  },
  async created() {
    await this.loadOptions();
    this.loadEvent();
  },
  beforeDestroy() {
    if (this.audiencePreviewTimer) clearTimeout(this.audiencePreviewTimer);
  },
  watch: {
    'form.course_id'(newVal, oldVal) {
      // When course changes, fetch course and populate title/description if empty
      if (!newVal) return;
      store.dispatch('events/getCourseById', { id: newVal }).then(res => {
        const course = res.data;
        this.selectedCourse = course;
        if (!this.form.title_override || this.form.title_override.trim() === '') {
          this.form.title_override = course.title || '';
        }
        if (!this.form.description_override || this.form.description_override.trim() === '') {
          this.form.description_override = course.summary || '';
        }
      }).catch(() => {});
    },
    // Mandatory Training bypasses the approval workflow entirely (participants
    // are auto-accepted), so both approval boxes are cleared and locked while
    // it is on.
    "form.is_pre_approved": {
      handler(isMandatory) {
        if (isMandatory) {
          this.form.require_manager_approval = false;
          this.form.require_admin_approval = false;
        }
      },
      immediate: true,
    },
    "form.company_ids": {
      handler() {
        this.queueAudiencePreview();
      },
      deep: true,
    },
    "form.site_ids": {
      handler() {
        this.queueAudiencePreview();
      },
      deep: true,
    },
    "form.user_ids": {
      handler() {
        this.queueAudiencePreview();
      },
      deep: true,
    },
    "form.role_ids": {
      handler() {
        this.queueAudiencePreview();
      },
      deep: true,
    },
  },
  methods: {
    queueAudiencePreview() {
      if (!this.loaded) return;
      if (this.audiencePreviewTimer) clearTimeout(this.audiencePreviewTimer);
      this.audiencePreviewTimer = setTimeout(() => {
        this.previewAudience();
      }, 350);
    },
    previewAudience() {
      if (!this.loaded) return;
      const payload = {
        companyIds: Array.isArray(this.form.company_ids) ? this.form.company_ids.filter(Boolean) : [],
        siteIds: Array.isArray(this.form.site_ids) ? this.form.site_ids.filter(Boolean) : [],
        roleIds: Array.isArray(this.form.role_ids) ? this.form.role_ids.filter(Boolean) : [],
        userIds: Array.isArray(this.form.user_ids) ? this.form.user_ids.filter(Boolean) : [],
      };

      const requestId = ++this.audiencePreviewRequestId;
      this.audiencePreview.loading = true;
      this.audiencePreview.error = null;
      store
        .dispatch("events/previewAudience", payload)
        .then((res) => {
          if (requestId !== this.audiencePreviewRequestId) return;
          const data = (res && res.data) || {};
          this.audiencePreview = {
            loading: false,
            count: data.count || 0,
            users: Array.isArray(data.users) ? data.users : [],
            error: null,
          };
        })
        .catch(() => {
          if (requestId !== this.audiencePreviewRequestId) return;
          this.audiencePreview.loading = false;
          this.audiencePreview.error = true;
          this.audiencePreview.count = 0;
          this.audiencePreview.users = [];
        });
    },
    async sendInvitations() {
      const confirm = await this.$bvModal.msgBoxConfirm(
        'Send invitation emails to all eligible audience members for this event?',
        {
          title: 'Send Invitations',
          okVariant: 'success',
          okTitle: 'Send',
          cancelTitle: 'Cancel',
          cancelVariant: 'outline-secondary',
          centered: true,
        }
      );
      if (!confirm) return;
      try {
        this.sendingInvitations = true;
        await store.dispatch('events/sendInvitations', { id: this.$route.params.id });
        this.$toast({
          component: ToastificationContent,
          props: { title: 'Invitations queued', icon: 'CheckIcon', variant: 'success' },
        });
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: { title: 'Error', text: e?.response?.data?.message || 'Failed to send invitations', icon: 'AlertTriangleIcon', variant: 'danger' },
        });
      } finally {
        this.sendingInvitations = false;
      }
    },
    canEditEvent() {
      return this.$CanEvent('edit_event', this.form || {});
    },
    canManageAttendance() {
      return this.$CanEvent('manage_event_attendance', this.form.admins || this.form || []);
    },
    dateFormat(val) {
      if (!val) return "-";
      const date = new Date(val);
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      const time = date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
      return `${day}-${month}-${year} ${time}`;
    },
    // Users deactivated since the event was saved are no longer offered in the
    // picker, so a stale id would render as a bare number. They are already
    // excluded from the resolved audience server-side, so dropping them here
    // only makes the form show what actually applies.
    // Guarded: if the options request failed we have no list to check against,
    // so keep the selection rather than silently clearing it on save.
    dropDeactivated(ids) {
      const selectable = (this.options && this.options.users) || [];
      if (!selectable.length) return ids;
      const allowed = new Set(selectable.map((u) => u.id));
      return ids.filter((id) => allowed.has(id));
    },
    async loadOptions() {
      try {
        const res = await store.dispatch("events/fetchEventOptions");
        this.options = res.data;
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Failed to load options",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      }
    },
    
    // Select All / Clear All methods for Companies
    selectAllCompanies() {
      this.form.company_ids = this.options.companies.map((c) => c.id);
    },
    clearAllCompanies() {
      this.form.company_ids = [];
    },
    // Select All / Clear All methods for Users
    selectAllUsers() {
      this.form.user_ids = this.options.users.map((u) => u.id);
    },
    clearAllUsers() {
      this.form.user_ids = [];
    },
    // Select All / Clear All methods for Sites
    selectAllSites() {
      const sites = this.options.sites || [];
      this.form.site_ids = sites.map((s) => s.id);
    },
    clearAllSites() {
      this.form.site_ids = [];
    },
    // Select All / Clear All methods for Roles
    selectAllRoles() {
      this.form.role_ids = this.options.roles.map((r) => r.id);
    },
    clearAllRoles() {
      this.form.role_ids = [];
    },
    async loadEvent() {
      try {
        this.loading = true;
        const id = this.$route.params.id;
        const res = await store.dispatch("events/getEventById", { id });
        const ev = res.data;
        this.form = { ...this.form, ...ev };
        this.selectedCourse = ev.course;
        // Initialize application method label/placeholder

        // If title/description are empty, default them from course
        if ((!this.form.title_override || this.form.title_override.trim() === '') && this.selectedCourse) {
          this.form.title_override = this.selectedCourse.title || '';
        }
        if ((!this.form.description_override || this.form.description_override.trim() === '') && this.selectedCourse) {
          this.form.description_override = this.selectedCourse.summary || '';
        }

        // Load existing audience
        this.form.company_ids = ev.companies?.map((c) => c.id) || [];
        this.form.site_ids = ev.sites?.map((s) => s.id) || [];
        this.form.user_ids = this.dropDeactivated(
          (ev.audience_users || [])?.map((u) => u.id) || []
        );
        this.form.role_ids = ev.roles?.map((r) => r.id) || [];
        const sessionsRes = await store.dispatch("events/listSessions", { id });
        this.sessions = sessionsRes.data.map((s) => ({
          id: s.id,
          sequence_no: s.sequence_no,
          start_date: s.start_at ? s.start_at.substring(0, 10) : "",
          start_time: s.start_at ? s.start_at.substring(11, 16) : "",
          end_date: s.end_at ? s.end_at.substring(0, 10) : "",
          end_time: s.end_at ? s.end_at.substring(11, 16) : "",
          venue_id: (s.venue && s.venue.id) || s.venue_id,
          meeting_url: s.meeting_url,
          meeting_passcode: s.meeting_passcode,
          primary_trainer_id:
            s.primary_trainer_id || (s.primaryTrainer && s.primaryTrainer.id),
          addons: this._deserializeAddons(
            s.addons || s.session_addons || s.add_ons || []
          ),
        }));
        if (this.sessions.length === 0) {
          this.addSession();
        }
        await this.loadMaterials();
        const appsRes = await store.dispatch("events/listApplications", { id });
        this.admin_user_ids = this.dropDeactivated(
          (ev.admins || []).map((u) => u.id)
        );

        // Load existing reminders
        this.reminders = ev.event_reminders || [];

        // Format application_deadline for date input (YYYY-MM-DD)
        if (ev.application_deadline) {
          const date = new Date(ev.application_deadline);
          this.form.application_deadline = date.toISOString().split("T")[0];
        }

        this.loaded = true;

        this.queueAudiencePreview();
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Failed to load event",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
        this.$router.push({ name: "events" });
      } finally {
        this.loading = false;
      }
    },
    getPartTitle(idx) {
      const title =
        this.form.title_override ||
        (this.selectedCourse ? this.selectedCourse.title : "");
      if (this.sessions.length <= 1) {
        return title || "Event timing";
      }
      return title ? `${title} - Part ${idx + 1}` : `Part ${idx + 1}`;
    },
    async loadMaterials() {
      const id = this.$route.params.id;
      try {
        const res = await store.dispatch("events/listMaterials", { id });
        this.materials = (res.data || []).map((m) => ({ ...m }));
      } catch (e) {
        /* silent */
      }
    },
    addSession() {
      this.sessions.push({
        sequence_no: this.sessions.length + 1,
        start_date: "",
        start_time: "",
        end_date: "",
        end_time: "",
        venue_id: null,
        meeting_url: "",
        meeting_passcode: "",
        primary_trainer_id: null,
        addons: [],
      });
    },
    async saveSession(s, showToast = true) {
      // Skip sessions if required date/time fields are missing
      if (!s.start_date || !s.start_time || !s.end_date || !s.end_time) {
        console.log("Skipping session with empty date/time:", s);
        return;
      }

      if (!this._validateSessionAddons(s)) {
        return;
      }

      // Validation is now done before calling this method

      const data = {
        sequence_no: s.sequence_no,
        start_at: this._combineDateTimeForBackend(s.start_date, s.start_time),
        end_at: this._combineDateTimeForBackend(s.end_date, s.end_time),
        venue_id: s.venue_id,
        meeting_url: s.meeting_url,
        meeting_passcode: s.meeting_passcode,
        primary_trainer_id: s.primary_trainer_id,
        addons: this._buildAddonsPayload(s),
      };

      if (s.id) {
        await store.dispatch("events/updateSession", { sessionId: s.id, data });
        if (showToast) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Session updated",
              icon: "ThumbsUpIcon",
              variant: "success",
            },
          });
        }
      } else {
        const res = await store.dispatch("events/addSession", {
          id: this.$route.params.id,
          data,
        });
        s.id = res.data.id;
        if (showToast) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Session added",
              icon: "ThumbsUpIcon",
              variant: "success",
            },
          });
        }
      }
    },
    async deleteSession(s) {
      if (!s.id) {
        this.sessions = this.sessions.filter((x) => x !== s);
        return;
      }
      const confirm = await this.$bvModal.msgBoxConfirm(
        "Delete this session?",
        {
          size: "sm",
          title: "Confirm",
          okVariant: "primary",
          okTitle: "Yes",
          cancelTitle: "No",
          cancelVariant: "outline-secondary",
          centered: true,
        }
      );
      if (!confirm) return;
      await store.dispatch("events/deleteSession", { sessionId: s.id });
      this.sessions = this.sessions.filter((x) => x.id !== s.id);
      this.$toast({
        component: ToastificationContent,
        props: {
          title: "Session deleted",
          icon: "ThumbsUpIcon",
          variant: "success",
        },
      });
    },
    async saveAdmins() {
      const id = this.$route.params.id;
      await store.dispatch("events/setEventAdmins", {
        id,
        admin_user_ids: this.admin_user_ids,
      });
      this.$toast({
        component: ToastificationContent,
        props: {
          title: "Event administrators updated",
          icon: "ThumbsUpIcon",
          variant: "success",
        },
      });
    },
    async onSubmit() {
      try {
        // Validate required fields
        if (!this.form.course_id) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Validation Error",
              text: "Please select a course",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
          return;
        }

        if (!this.form.year) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Validation Error",
              text: "Please enter a year",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
          return;
        }

        if (!this.form.time_zone) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Validation Error",
              text: "Please enter a time zone",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
          return;
        }

        if (
          !this.form.title_override ||
          this.form.title_override.trim() === ""
        ) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Validation Error",
              text: "Please enter a title",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
          return;
        }

        // An event must define how participants get in: either an approval
        // workflow (manager and/or admin) or Mandatory Training.
        if (this.missingApprovalSelection) {
          this.approvalWarningShown = true;
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Validation Error",
              text: "Please select at least one of: Require manager approval, Require admin approval, or Mandatory Training",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
          return;
        }

        // Validate application description for non-calendar methods
        // if (
        //   this.form.application_method &&
        //   this.form.application_method.toLowerCase() !== "calendar"
        // ) {
        //   if (!this.form.application_description) {
        //     this.$toast({
        //       component: ToastificationContent,
        //       props: {
        //         title: "Validation Error",
        //         text: "Please provide application details",
        //         icon: "AlertTriangleIcon",
        //         variant: "danger",
        //       },
        //     });
        //     return;
        //   }
        //   if (
        //     this.form.application_method.toLowerCase() === "email" &&
        //     !/^([^\s@]+)@([^\s@]+)\.[^\s@]+$/.test(
        //       this.form.application_description.trim()
        //     )
        //   ) {
        //     this.$toast({
        //       component: ToastificationContent,
        //       props: {
        //         title: "Validation Error",
        //         text: "Please enter a valid application email address",
        //         icon: "AlertTriangleIcon",
        //         variant: "danger",
        //       },
        //     });
        //     return;
        //   }
        //   if (
        //     this.form.application_method.toLowerCase() === "external" &&
        //     !/^(https?:\/\/)?([\w.-]+)\.([a-z\.]{2,6})([\/\w .-]*)*\/?$/i.test(
        //       this.form.application_description.trim()
        //     )
        //   ) {
        //     this.$toast({
        //       component: ToastificationContent,
        //       props: {
        //         title: "Validation Error",
        //         text: "Please enter a valid application website URL",
        //         icon: "AlertTriangleIcon",
        //         variant: "danger",
        //       },
        //     });
        //     return;
        //   }
        // }

        // Validate all sessions before saving anything
        for (const s of this.sessions) {
          // Skip sessions with empty required fields
          if (!s.start_date || !s.start_time || !s.end_date || !s.end_time) {
            continue;
          }

          const st = this._normalizeTime(s.start_time);
          const et = this._normalizeTime(s.end_time);
          const start = this._toDateObj(`${s.start_date}T${st}:00`);
          const end = this._toDateObj(`${s.end_date}T${et}:00`);
          if (!st || !et || !start || !end || end <= start) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: "Session Date Error",
                text: `Part ${s.sequence_no}: End time must be after start time`,
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
            return; // Stop here, don't save anything
          }

          if (!this._validateSessionAddons(s)) {
            return;
          }
        }
                // Warn if status is draft before updating
        if ((this.form.status || '').toLowerCase() === 'draft') {
          const result = await this.$swal({
            title: 'Event is in draft',
            text:
              "Users won't be able to view this event until the status is set to 'open'. Do you want to proceed?",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Proceed',
            cancelButtonText: 'Cancel',
            reverseButtons: true,
          });
          if (!result.isConfirmed) {
            return;
          }
        }
        

        this.loading = true;
        const id = this.$route.params.id;

        // Update event
        await store.dispatch("events/updateEvent", {
          id,
          data: { ...this.form, reminders: this.reminders },
        });

        // Update all sessions
        for (const s of this.sessions) {
          await this.saveSession(s, false);
        }

        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Event updated",
            icon: "ThumbsUpIcon",
            variant: "success",
          },
        });
        this.$router.push({ name: "events" });
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Update failed",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        this.loading = false;
      }
    },

    async uploadMaterial() {
      try {
        this.loading = true;
        const id = this.$route.params.id;
        // Validate visible date order
        if (this.materialForm.visible_from && this.materialForm.visible_to) {
          const from = new Date(this.materialForm.visible_from);
          const to = new Date(this.materialForm.visible_to);
          if (to < from) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Material Date Error',
                text: 'Visible to date must be on or after Visible from date',
                icon: 'AlertTriangleIcon',
                variant: 'danger',
              },
            });
            this.loading = false;
            return;
          }
        }
        const fd = new FormData();
        fd.append("title", this.materialForm.title || "");
        if (this.materialForm.file) {
          fd.append("file", this.materialForm.file);
        }
        if (this.materialForm.external_url) {
          fd.append("external_url", this.materialForm.external_url);
        }
        if (this.materialForm.visible_from) {
          fd.append("visible_from", this.materialForm.visible_from);
        }
        if (this.materialForm.visible_to) {
          fd.append("visible_to", this.materialForm.visible_to);
        }
        fd.append(
          "is_visible_to_accepted_only",
          this.materialForm.is_visible_to_accepted_only ? 1 : 0
        );
        if (this.materialForm.course_id) {
          fd.append("course_id", this.materialForm.course_id);
        }
        const res = await store.dispatch("events/uploadMaterial", {
          id,
          data: fd,
        });
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Material uploaded",
            icon: "ThumbsUpIcon",
            variant: "success",
          },
        });
        this.materialForm = {
          title: "",
          file: null,
          external_url: "",
          visible_from: "",
          visible_to: "",
          is_visible_to_accepted_only: true,
          course_id: null,
        };
        await this.loadMaterials();
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Upload failed",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        this.loading = false;
      }
    },
    editMaterialStart(m) {
      this.editingId = m.id;
      this.editRow = {
        title: m.title,
        external_url: m.external_url,
        visible_from: m.visible_from,
        visible_to: m.visible_to,
        is_visible_to_accepted_only: !!m.is_visible_to_accepted_only,
        course_id: m.course_id || null,
        file: null,
      };
    },
    editMaterialCancel() {
      this.editingId = null;
      this.editRow = {
        title: "",
        external_url: "",
        visible_from: "",
        visible_to: "",
        is_visible_to_accepted_only: true,
        course_id: null,
        file: null,
      };
    },
    async editMaterialSave(m) {
      try {
        this.loading = true;
        // Validate visible date order
        if (this.editRow.visible_from && this.editRow.visible_to) {
          const from = new Date(this.editRow.visible_from);
          const to = new Date(this.editRow.visible_to);
          if (to < from) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Material Date Error',
                text: 'Visible to date must be on or after Visible from date',
                icon: 'AlertTriangleIcon',
                variant: 'danger',
              },
            });
            this.loading = false;
            return;
          }
        }
        const fd = new FormData();
        if (this.editRow.title) {
          fd.append("title", this.editRow.title);
        }
        if (this.editRow.external_url) {
          fd.append("external_url", this.editRow.external_url);
        }
        if (this.editRow.visible_from) {
          fd.append("visible_from", this.editRow.visible_from);
        }
        if (this.editRow.visible_to) {
          fd.append("visible_to", this.editRow.visible_to);
        }
        if (typeof this.editRow.is_visible_to_accepted_only !== "undefined") {
          fd.append(
            "is_visible_to_accepted_only",
            this.editRow.is_visible_to_accepted_only ? 1 : 0
          );
        }
        if (this.editRow.course_id) {
          fd.append("course_id", this.editRow.course_id);
        }
        if (this.editRow.file) {
          fd.append("file", this.editRow.file);
        }
        await store.dispatch("events/updateMaterial", {
          materialId: m.id,
          data: fd,
        });
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Material updated",
            icon: "ThumbsUpIcon",
            variant: "success",
          },
        });
        this.editMaterialCancel();
        await this.loadMaterials();
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Update failed",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        this.loading = false;
      }
    },
    async deleteMaterial(m) {
      const ok = await this.$bvModal.msgBoxConfirm("Delete this material?", {
        size: "sm",
        title: "Confirm",
        okVariant: "primary",
        okTitle: "Yes",
        cancelTitle: "No",
        cancelVariant: "outline-secondary",
        centered: true,
      });
      if (!ok) return;
      try {
        this.loading = true;
        await store.dispatch("events/deleteMaterial", { materialId: m.id });
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Material deleted",
            icon: "ThumbsUpIcon",
            variant: "success",
          },
        });
        await this.loadMaterials();
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Delete failed",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        this.loading = false;
      }
    },
    // Modal methods
    openAddVenueModal(sessionIndex) {
      this.currentSessionIndex = sessionIndex;
      this.showAddVenueModal = true;
    },
    openAddTrainerModal(sessionIndex) {
      this.currentSessionIndex = sessionIndex;
      this.showAddTrainerModal = true;
    },
    async handleAddVenue(bvModalEvent) {
      bvModalEvent.preventDefault();
      if (!this.newVenue.name.trim()) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Please fill venue name",
            icon: "AlertTriangleIcon",
            variant: "warning",
          },
        });
        return;
      }

      try {
        const response = await store.dispatch(
          "settings/createVenue",
          this.newVenue
        );
        const newVenue = response.data.data;
        this.options.venues.push(newVenue);

        // Auto-select the newly added venue in the current session
        if (
          this.currentSessionIndex !== null &&
          this.sessions[this.currentSessionIndex]
        ) {
          this.sessions[this.currentSessionIndex].venue_id = newVenue.id;
        }
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Venue added successfully",
            icon: "ThumbsUpIcon",
            variant: "success",
          },
        });
        this.resetVenueForm();
        this.showAddVenueModal = false;
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error adding venue",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      }
    },
    async handleAddTrainer(bvModalEvent) {
      bvModalEvent.preventDefault();
      if (!this.newTrainer.name.trim() || !this.newTrainer.email.trim()) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Please fill all required trainer fields",
            icon: "AlertTriangleIcon",
            variant: "warning",
          },
        });
        return;
      }

      // Email format validation
      const emailRegex = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i;
      if (!emailRegex.test(this.newTrainer.email.trim())) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Please enter a valid email address",
            icon: "AlertTriangleIcon",
            variant: "warning",
          },
        });
        return;
      }

      try {
        // Create FormData for file upload
        const formData = new FormData();
        formData.append("name", this.newTrainer.name.trim());
        formData.append("email", this.newTrainer.email.trim());
        if (this.newTrainer.phone.trim()) {
          formData.append("phone", this.newTrainer.phone.trim());
        }
        if (this.newTrainerCV) {
          formData.append("cv_pdf", this.newTrainerCV);
        }

        // Add to options via API call
        const response = await store.dispatch(
          "settings/createTrainerWithFile",
          formData
        );
        const newTrainer = response.data.data;
        this.options.trainers.push(newTrainer);

        // Auto-select the newly added trainer in the current session
        if (
          this.currentSessionIndex !== null &&
          this.sessions[this.currentSessionIndex]
        ) {
          this.sessions[this.currentSessionIndex].primary_trainer_id =
            newTrainer.id;
        }
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Trainer added successfully",
            icon: "ThumbsUpIcon",
            variant: "success",
          },
        });
        this.resetTrainerForm();
        this.showAddTrainerModal = false;
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error adding trainer",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      }
    },
    resetVenueForm() {
      this.newVenue = {
        name: "",
      };
    },
    resetTrainerForm() {
      this.newTrainer = {
        name: "",
        email: "",
        phone: "",
      };
      this.newTrainerCV = null;
    },
    // Reminder Methods
    addReminder() {
      this.reminders.push({
        days_before: 3,
        message: "",
        type: "event_reminder",
        is_active: true,
      });
    },
    removeReminder(index) {
      this.reminders.splice(index, 1);
    },
    materialFileHref(path) {
      const base = this.materialsURL || '';
      return base.replace(/\/$/, '') + '/' + String(path || '').replace(/^\//, '');
    },
    // Helpers for datetime parsing/formatting
    _toDateObj(val) {
      if (!val || typeof val !== 'string') return null;
      const trimmed = val.trim();
      const iso = trimmed.includes('T') ? trimmed : trimmed.replace(' ', 'T');
      const d = new Date(iso);
      return isNaN(d.getTime()) ? null : d;
    },
    _formatDateTimeForBackend(val) {
      if (!val || typeof val !== 'string') return null;
      const t = val.trim().replace('T', ' ');
      if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/.test(t)) return `${t}:00`;
      if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(t)) return t;
      const maybe = t.replace(/\s+/, ' ');
      return maybe;
    },
    // Helpers for datetime parsing/formatting
    _toDateObj(val) {
      if (!val || typeof val !== 'string') return null;
      const d = new Date(val);
      return isNaN(d.getTime()) ? null : d;
    },
    _normalizeTime(time) {
      if (!time || typeof time !== 'string') return null;
      const t = time.trim();
      if (/^\d{1,2}:\d{2}$/.test(t)) return this._padTime(t);
      if (/^\d{3,4}$/.test(t)) {
        const part = t.length === 3 ? `0${t}` : t;
        const hh = part.slice(0, 2);
        const mm = part.slice(2);
        return this._padTime(`${hh}:${mm}`);
      }
      return null;
    },
    _padTime(tt) {
      const [h, m] = tt.split(':');
      const hh = String(parseInt(h, 10)).padStart(2, '0');
      const mm = String(parseInt(m, 10)).padStart(2, '0');
      if (+hh < 0 || +hh > 23 || +mm < 0 || +mm > 59) return null;
      return `${hh}:${mm}`;
    },
    _combineDateTimeForBackend(date, time) {
      const t = this._normalizeTime(time);
      if (!date || !t) return null;
      return `${date} ${t}:00`;
    },
    onTimeBlur(session, key) {
      const norm = this._normalizeTime(session[key]);
      if (norm) session[key] = norm;
    },

    addAddon(session) {
      if (!session.addons) this.$set(session, 'addons', []);
      session.addons.push({
        id: null,
        code: "",
        label: "",
        starts_time: "",
        ends_time: "",
        is_required: false,
        sort_order: null,
      });
    },
    removeAddon(session, addonIndex) {
      if (!session.addons) return;
      session.addons.splice(addonIndex, 1);
    },
    onAddonTimeBlur(addon, key) {
      const norm = this._normalizeTime(addon[key]);
      if (norm) addon[key] = norm;
    },
    _splitDateTime(val) {
      if (!val || typeof val !== 'string') return { date: '', time: '' };
      const trimmed = val.trim();
      const iso = trimmed.includes('T') ? trimmed : trimmed.replace(' ', 'T');
      return {
        date: iso.length >= 10 ? iso.substring(0, 10) : '',
        time: iso.length >= 16 ? iso.substring(11, 16) : '',
      };
    },
    _deserializeAddons(raw) {
      const arr = Array.isArray(raw) ? raw : [];
      return arr.map((a) => {
        const starts = this._splitDateTime(a.starts_at);
        const ends = this._splitDateTime(a.ends_at);
        const sortOrder =
          a.sort_order !== undefined && a.sort_order !== null
            ? a.sort_order
            : (a.sort !== undefined && a.sort !== null ? a.sort : null);
        return {
          id: a.id || null,
          code: a.code || '',
          label: a.label || '',
          starts_time: starts.time,
          ends_time: ends.time,
          is_required: !!(a.is_required || a.required),
          sort_order: sortOrder,
        };
      });
    },
    _buildAddonsPayload(session) {
      const list = Array.isArray(session.addons) ? session.addons : [];
      const out = [];
      for (let i = 0; i < list.length; i++) {
        const a = list[i] || {};
        const code = String(a.code || '').trim().toLowerCase();
        const label = String(a.label || '').trim();
        const hasAny =
          code ||
          label ||
          a.starts_time ||
          a.ends_time;
        if (!hasAny) continue;

        const starts_at =
          session.start_date && a.starts_time
            ? this._combineDateTimeForBackend(session.start_date, a.starts_time)
            : null;
        const ends_at =
          session.end_date && a.ends_time
            ? this._combineDateTimeForBackend(session.end_date, a.ends_time)
            : null;

        const payload = {
          code,
          label,
          starts_at,
          ends_at,
        };

        if (a.id) payload.id = a.id;
        if (typeof a.is_required === 'boolean') payload.is_required = a.is_required;

        const sortOrder =
          a.sort_order !== undefined && a.sort_order !== null
            ? a.sort_order
            : (a.sort !== undefined && a.sort !== null ? a.sort : null);
        if (sortOrder !== null) payload.sort_order = sortOrder;
        else payload.sort_order = i + 1;

        out.push(payload);
      }
      return out;
    },
    _validateSessionAddons(session) {
      const list = Array.isArray(session.addons) ? session.addons : [];
      for (let i = 0; i < list.length; i++) {
        const a = list[i] || {};
        const code = String(a.code || '').trim();
        const label = String(a.label || '').trim();
        const hasAny =
          code ||
          label ||
          a.starts_time ||
          a.ends_time;
        if (!hasAny) continue;

        if (!code || !label) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Add-on Validation Error",
              text: `Part ${session.sequence_no}, Add-on ${i + 1}: Code and Label are required`,
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
          return false;
        }
        if (code.length > 50) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Add-on Validation Error",
              text: `Part ${session.sequence_no}, Add-on ${i + 1}: Code must be 50 characters or less`,
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
          return false;
        }
        if (label.length > 255) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Add-on Validation Error",
              text: `Part ${session.sequence_no}, Add-on ${i + 1}: Label must be 255 characters or less`,
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
          return false;
        }

        if (a.starts_time && !session.start_date) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Add-on Date Error",
              text: `Part ${session.sequence_no}, Add-on ${i + 1}: Session start date is required when add-on start time is set`,
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
          return false;
        }
        if (a.ends_time && !session.end_date) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Add-on Date Error",
              text: `Part ${session.sequence_no}, Add-on ${i + 1}: Session end date is required when add-on end time is set`,
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
          return false;
        }

        if (session.start_date && a.starts_time && session.end_date && a.ends_time) {
          const st = this._normalizeTime(a.starts_time);
          const et = this._normalizeTime(a.ends_time);
          const start = this._toDateObj(`${session.start_date}T${st}:00`);
          const end = this._toDateObj(`${session.end_date}T${et}:00`);
          if (!st || !et || !start || !end || end <= start) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: "Add-on Date Error",
                text: `Part ${session.sequence_no}, Add-on ${i + 1}: Ends must be after starts`,
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
            return false;
          }
        }
      }
      return true;
    },
  },
};
</script>
<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/quill.scss";

.section-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;
  padding: 1rem;
}

.parts-section {
  background: #f5f8ff;
  border: 1px solid #dbe6ff;
  border-radius: 0.5rem;
  padding: 1rem;
}

.parts-header {
  padding-bottom: 0.5rem;
  border-bottom: 1px dashed #cdd9ff;
  margin-bottom: 0.75rem;
}

.session-card-edit {
  background: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;
  padding: 0.75rem;
}

.session-edit-header {
  padding-bottom: 0.25rem;
  border-bottom: 1px dashed #e9ecef;
}
</style>
